import { useState, useEffect, useRef } from "react";
import { User, Mail, Lock, Camera, Save, Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { account, storage, BUCKET_ID, ID, ENDPOINT, PROJECT_ID } from "../../../lib/appwrite";
import type { Models } from "appwrite";

type UserDoc = Models.User<Models.Preferences>;

type Toast = { type: "success" | "error"; message: string };

const AVATAR_PREF_KEY = "avatarDataUrl";

export function AdminProfile() {
    const [user, setUser] = useState<UserDoc | null>(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<Toast | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    // Form states
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [emailPassword, setEmailPassword] = useState("");
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

    // Loading per section
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);
    const [savingPwd, setSavingPwd] = useState(false);
    const [savingAvatar, setSavingAvatar] = useState(false);

    const showToast = (type: Toast["type"], message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    useEffect(() => {
        account.get().then((u) => {
            setUser(u);
            setName(u.name || "");
            setEmail(u.email || "");
            // Load saved avatar from prefs
            const saved = u.prefs?.[AVATAR_PREF_KEY] as string | undefined;
            if (saved) setAvatarUrl(saved);
            setLoading(false);
        });
    }, []);

    const initials = (user?.name || user?.email || "A")
        .split(/[\s@]/).filter(Boolean).slice(0, 2)
        .map((w) => w[0].toUpperCase()).join("");

    // ── Avatar upload ──────────────────────────────────────────────────────────
    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            showToast("error", "Image must be under 2 MB.");
            return;
        }
        setSavingAvatar(true);
        try {
            // Upload to Appwrite Storage
            const result = await storage.createFile(BUCKET_ID, ID.unique(), file);

            const dataUrl = `${ENDPOINT}/storage/buckets/${BUCKET_ID}/files/${result.$id}/view?project=${PROJECT_ID}`;

            // Save URL to Account Preferences
            const updated = await account.updatePrefs({ ...user?.prefs, [AVATAR_PREF_KEY]: dataUrl });
            setUser(updated as any);
            setAvatarUrl(dataUrl);
            showToast("success", "Avatar uploaded & saved!");
        } catch (err: any) {
            console.error(err);
            showToast("error", err?.message || "Failed to save avatar.");
        } finally {
            setSavingAvatar(false);
            if (fileRef.current) fileRef.current.value = ""; // Reset input
        }
    };

    // ── Save name ──────────────────────────────────────────────────────────────
    const handleSaveName = async () => {
        if (!name.trim()) return showToast("error", "Name cannot be empty.");
        setSavingProfile(true);
        try {
            const updated = await account.updateName(name.trim());
            setUser(updated);
            showToast("success", "Name updated successfully.");
        } catch {
            showToast("error", "Failed to update name.");
        } finally {
            setSavingProfile(false);
        }
    };

    // ── Save email ─────────────────────────────────────────────────────────────
    const handleSaveEmail = async () => {
        if (!email.trim()) return showToast("error", "Email cannot be empty.");
        if (!emailPassword) return showToast("error", "Current password is required to change email.");
        setSavingEmail(true);
        try {
            const updated = await account.updateEmail(email.trim(), emailPassword);
            setUser(updated);
            setEmailPassword("");
            showToast("success", "Email updated successfully.");
        } catch (err: any) {
            showToast("error", err?.message || "Failed to update email.");
        } finally {
            setSavingEmail(false);
        }
    };

    // ── Save password ──────────────────────────────────────────────────────────
    const handleSavePwd = async () => {
        if (!currentPwd) return showToast("error", "Current password is required.");
        if (!newPwd || newPwd.length < 8) return showToast("error", "New password must be at least 8 characters.");
        if (newPwd !== confirmPwd) return showToast("error", "Passwords do not match.");
        setSavingPwd(true);
        try {
            await account.updatePassword(newPwd, currentPwd);
            setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
            showToast("success", "Password changed successfully.");
        } catch (err: any) {
            showToast("error", err?.message || "Failed to change password.");
        } finally {
            setSavingPwd(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24 min-h-[50vh]">
                <Loader2 className="w-7 h-7 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl space-y-4">
            {/* ── Page header ── */}
            <div className="pb-3 border-b border-border">
                <h2 className="text-base font-semibold text-foreground">Profile Settings</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Manage your account details and security.</p>
            </div>

            {/* ── Toast ── */}
            {toast && (
                <div className={`flex items-center gap-2.5 px-4 py-3 rounded border text-sm font-medium ${toast.type === "success"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-red-50 border-red-200 text-red-700"
                    }`}>
                    {toast.type === "success"
                        ? <CheckCircle className="w-4 h-4 shrink-0" />
                        : <AlertCircle className="w-4 h-4 shrink-0" />}
                    {toast.message}
                </div>
            )}

            {/* ── Avatar ── */}
            <div className="p-5 border border-border rounded bg-card">
                <p className="text-sm font-semibold text-foreground mb-4">Profile Photo</p>
                <div className="flex items-center gap-5">
                    <div className="relative shrink-0">
                        {avatarUrl ? (
                            <img src={avatarUrl} alt="Avatar" className="h-20 w-20 rounded-full object-cover border-2 border-border" />
                        ) : (
                            <div className="h-20 w-20 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold border-2 border-primary/20">
                                {initials}
                            </div>
                        )}
                        <button
                            onClick={() => fileRef.current?.click()}
                            disabled={savingAvatar}
                            className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shadow-sm"
                        >
                            {savingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-foreground">{user?.name || "Admin"}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
                        <p className="text-xs text-muted-foreground/70 mt-2">JPG, PNG or WebP · Max 2 MB</p>
                        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={savingAvatar} className="mt-2.5 h-7 text-xs shadow-none">
                            {savingAvatar ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Saving…</> : "Change Photo"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* ── Name ── */}
            <div className="p-5 border border-border rounded bg-card space-y-4">
                <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">Display Name</p>
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-xs text-muted-foreground">Full Name</Label>
                    <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your full name"
                        className="h-9 text-sm shadow-none border-border"
                    />
                </div>
                <div className="flex justify-end">
                    <Button size="sm" onClick={handleSaveName} disabled={savingProfile || name === (user?.name || "")} className="h-8 gap-1.5">
                        {savingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save Name
                    </Button>
                </div>
            </div>

            {/* ── Email ── */}
            <div className="p-5 border border-border rounded bg-card space-y-4">
                <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">Email Address</p>
                </div>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="email" className="text-xs text-muted-foreground">New Email</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="h-9 text-sm shadow-none border-border"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="email-pwd" className="text-xs text-muted-foreground">Current Password <span className="text-destructive">*</span></Label>
                        <Input
                            id="email-pwd"
                            type="password"
                            value={emailPassword}
                            onChange={(e) => setEmailPassword(e.target.value)}
                            placeholder="Required to change email"
                            className="h-9 text-sm shadow-none border-border"
                        />
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button size="sm" onClick={handleSaveEmail} disabled={savingEmail || !emailPassword || email === user?.email} className="h-8 gap-1.5">
                        {savingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save Email
                    </Button>
                </div>
            </div>

            {/* ── Password ── */}
            <div className="p-5 border border-border rounded bg-card space-y-4">
                <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">Change Password</p>
                </div>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="current-pwd" className="text-xs text-muted-foreground">Current Password</Label>
                        <div className="relative">
                            <Input
                                id="current-pwd"
                                type={showCurrent ? "text" : "password"}
                                value={currentPwd}
                                onChange={(e) => setCurrentPwd(e.target.value)}
                                placeholder="••••••••"
                                className="h-9 text-sm pr-9 shadow-none border-border"
                            />
                            <button type="button" onClick={() => setShowCurrent((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                {showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="new-pwd" className="text-xs text-muted-foreground">New Password</Label>
                        <div className="relative">
                            <Input
                                id="new-pwd"
                                type={showNew ? "text" : "password"}
                                value={newPwd}
                                onChange={(e) => setNewPwd(e.target.value)}
                                placeholder="Min. 8 characters"
                                className="h-9 text-sm pr-9 shadow-none border-border"
                            />
                            <button type="button" onClick={() => setShowNew((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                {showNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="confirm-pwd" className="text-xs text-muted-foreground">Confirm New Password</Label>
                        <Input
                            id="confirm-pwd"
                            type="password"
                            value={confirmPwd}
                            onChange={(e) => setConfirmPwd(e.target.value)}
                            placeholder="Repeat new password"
                            className={`h-9 text-sm shadow-none border-border ${confirmPwd && confirmPwd !== newPwd ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
                        />
                        {confirmPwd && confirmPwd !== newPwd && (
                            <p className="text-xs text-destructive">Passwords do not match</p>
                        )}
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button size="sm" onClick={handleSavePwd} disabled={savingPwd || !currentPwd || !newPwd || newPwd !== confirmPwd} className="h-8 gap-1.5">
                        {savingPwd ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
                        Update Password
                    </Button>
                </div>
            </div>

            {/* ── Account metadata ── */}
            <div className="p-4 border border-border rounded bg-muted/30">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2">Account Info</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                    <div className="text-muted-foreground">Account ID</div>
                    <div className="font-mono text-foreground/70 truncate">{user?.$id}</div>
                    <div className="text-muted-foreground">Created</div>
                    <div className="text-foreground/70">{user?.$createdAt ? new Date(user.$createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</div>
                    <div className="text-muted-foreground">Email verified</div>
                    <div className={user?.emailVerification ? "text-emerald-600 font-medium" : "text-amber-600 font-medium"}>
                        {user?.emailVerification ? "Yes" : "No"}
                    </div>
                </div>
            </div>
        </div>
    );
}
